import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import type { Question } from "@/types";
import { Card } from "@/components/ui/Card";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { Spinner } from "@/components/ui/Spinner";

export default function QuestionManager() {
  const { id = "" } = useParams<{ id: string }>();
  const [questions, setQuestions] = useState<Question[]>([]);
  const [loading, setLoading] = useState(true);

  const [showAi, setShowAi] = useState(false);
  const [argomento, setArgomento] = useState("");
  const [sezione, setSezione] = useState("Modulo 1");
  const [livello, setLivello] = useState<"base" | "intermedio" | "avanzato">("intermedio");
  const [numeroChiuse, setNumeroChiuse] = useState(5);
  const [numeroAperte, setNumeroAperte] = useState(2);
  const [aiSending, setAiSending] = useState(false);
  const [aiError, setAiError] = useState<string | null>(null);

  const [showManual, setShowManual] = useState(false);
  const [editingQuestion, setEditingQuestion] = useState<Question | null>(null);
  const [mTesto, setMTesto] = useState("");
  const [mTipo, setMTipo] = useState<"chiusa" | "aperta" | "scala">("chiusa");
  const [mOpzioni, setMOpzioni] = useState<string[]>(["", "", "", ""]);
  const [mRispostaCorretta, setMRispostaCorretta] = useState<number>(0);
  const [mSezione, setMSezione] = useState("");
  const [mSpiegazione, setMSpiegazione] = useState("");
  const [mSending, setMSending] = useState(false);
  const [mError, setMError] = useState<string | null>(null);

  function load() {
    setLoading(true);
    api.get<Question[]>(`/admin/tests/${id}/questions`)
      .then(setQuestions)
      .finally(() => setLoading(false));
  }
  useEffect(load, [id]);

  async function generate(ev: React.FormEvent) {
    ev.preventDefault();
    setAiSending(true); setAiError(null);
    try {
      await api.post(`/admin/tests/${id}/generate`, {
        argomento, sezione, livello, numeroChiuse, numeroAperte,
      });
      setShowAi(false);
      load();
    } catch (e: any) {
      setAiError(e.message);
    } finally {
      setAiSending(false);
    }
  }

  function openEdit(q: Question) {
    setEditingQuestion(q);
    setMTesto(q.testo);
    setMTipo(q.tipo);
    setMOpzioni(q.opzioni?.length ? [...q.opzioni] : ["", "", "", ""]);
    setMRispostaCorretta(q.rispostaCorretta ?? 0);
    setMSezione(q.sezione ?? "");
    setMSpiegazione(q.spiegazione ?? "");
    setShowManual(true);
    setShowAi(false);
  }

  function closeManual() {
    setShowManual(false);
    setEditingQuestion(null);
    setMTesto(""); setMTipo("chiusa");
    setMOpzioni(["", "", "", ""]);
    setMRispostaCorretta(0); setMSezione(""); setMSpiegazione("");
    setMError(null);
  }

  async function submitManual(ev: React.FormEvent) {
    ev.preventDefault();
    setMSending(true); setMError(null);
    try {
      const body: Record<string, unknown> = {
        testo: mTesto.trim(),
        tipo: mTipo,
        sezione: mSezione.trim() || null,
        spiegazione: mSpiegazione.trim() || null,
      };
      if (mTipo === "chiusa") {
        const opzioniFiltrate = mOpzioni.map(o => o.trim()).filter(Boolean);
        if (opzioniFiltrate.length < 2) { setMError("Servono almeno 2 opzioni per una domanda chiusa"); setMSending(false); return; }
        body.opzioni = opzioniFiltrate;
        body.rispostaCorretta = mRispostaCorretta;
      }
      if (editingQuestion) {
        await api.put(`/admin/questions/${editingQuestion.id}`, body);
      } else {
        await api.post(`/admin/tests/${id}/questions`, body);
      }
      closeManual();
      load();
    } catch (e: any) {
      setMError(e.message);
    } finally {
      setMSending(false);
    }
  }

  async function approve(qid: string) {
    await api.post(`/admin/questions/${qid}/approve`); load();
  }
  async function reject(qid: string) {
    if (!confirm("Eliminare questa domanda?")) return;
    await api.post(`/admin/questions/${qid}/reject`); load();
  }
  async function regenerate(qid: string) {
    if (!confirm("Rigenerare con AI? La versione corrente verrà sostituita.")) return;
    try { await api.post(`/admin/questions/${qid}/regenerate`); load(); }
    catch (e: any) { alert(e.message); }
  }
  async function del(qid: string) {
    if (!confirm("Eliminare questa domanda?")) return;
    await api.delete(`/admin/questions/${qid}`); load();
  }

  return (
    <div className="max-w-4xl mx-auto px-4 py-8 space-y-5">
      <header className="flex justify-between items-baseline">
        <h1 className="text-2xl font-serif text-primary">Domande</h1>
        <div className="flex gap-2">
          <Button
            onClick={() => {
              if (showManual && !editingQuestion) {
                closeManual();
              } else {
                closeManual();
                setShowManual(true);
                setShowAi(false);
              }
            }}
            variant="secondary"
          >
            {showManual && !editingQuestion ? "Annulla" : "+ Aggiungi"}
          </Button>
          <Button onClick={() => { setShowAi(!showAi); closeManual(); }} variant="secondary">
            {showAi ? "Annulla AI" : "✨ Genera con AI"}
          </Button>
          <Link to="/admin/tests" className="font-mono text-xs text-secondary hover:text-primary self-center">← test</Link>
        </div>
      </header>

      {showManual && (
        <Card>
          <form onSubmit={submitManual} className="space-y-3">
            <h2 className="font-mono text-xs uppercase tracking-wider text-secondary">
              {editingQuestion ? "Modifica domanda" : "Nuova domanda"}
            </h2>
            <div className="flex flex-col gap-1.5">
              <label className="text-xs font-mono uppercase tracking-wider text-secondary">Testo domanda</label>
              <textarea
                value={mTesto}
                onChange={(e) => setMTesto(e.target.value)}
                required
                rows={3}
                className="w-full bg-bg border border-border rounded-lg px-4 py-3 text-primary focus:outline-none focus:border-accent resize-none"
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <label className="text-xs font-mono uppercase tracking-wider text-secondary">Tipo</label>
              <select
                value={mTipo}
                onChange={(e) => setMTipo(e.target.value as "chiusa" | "aperta" | "scala")}
                className="bg-bg border border-border rounded-lg px-4 py-3 text-primary"
              >
                <option value="chiusa">Chiusa</option>
                <option value="aperta">Aperta</option>
                <option value="scala">Scala</option>
              </select>
            </div>
            {mTipo === "chiusa" && (
              <div className="space-y-2">
                {mOpzioni.map((op, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <input
                      type="radio"
                      name="rispostaCorretta"
                      checked={mRispostaCorretta === i}
                      onChange={() => setMRispostaCorretta(i)}
                      className="accent-accent"
                      aria-label={`Opzione ${String.fromCharCode(65 + i)} corretta`}
                    />
                    <Input
                      label=""
                      value={op}
                      onChange={(e) => {
                        const next = [...mOpzioni];
                        next[i] = e.target.value;
                        setMOpzioni(next);
                      }}
                      placeholder={`Opzione ${String.fromCharCode(65 + i)}`}
                    />
                  </div>
                ))}
              </div>
            )}
            <Input label="Sezione" value={mSezione} onChange={(e) => setMSezione(e.target.value)} placeholder="Opzionale" />
            <div className="flex flex-col gap-1.5">
              <label className="text-xs font-mono uppercase tracking-wider text-secondary">Spiegazione (opzionale)</label>
              <textarea
                value={mSpiegazione}
                onChange={(e) => setMSpiegazione(e.target.value)}
                rows={2}
                className="w-full bg-bg border border-border rounded-lg px-4 py-3 text-primary focus:outline-none focus:border-accent resize-none"
              />
            </div>
            {mError && <div className="text-red-300 text-sm">{mError}</div>}
            <div className="flex gap-2">
              <Button type="submit" loading={mSending}>Salva domanda</Button>
              <Button type="button" variant="secondary" onClick={closeManual}>Annulla</Button>
            </div>
          </form>
        </Card>
      )}

      {showAi && (
        <Card>
          <form onSubmit={generate} className="space-y-3">
            <h2 className="font-mono text-xs uppercase tracking-wider text-secondary">Genera con Claude</h2>
            <Input label="Argomento" value={argomento} onChange={(e) => setArgomento(e.target.value)} required />
            <div className="grid grid-cols-2 gap-3">
              <Input label="Sezione" value={sezione} onChange={(e) => setSezione(e.target.value)} required />
              <div className="flex flex-col gap-1.5">
                <label className="text-xs font-mono uppercase tracking-wider text-secondary">Livello</label>
                <select
                  value={livello}
                  onChange={(e) => setLivello(e.target.value as any)}
                  className="bg-bg border border-border rounded-lg px-4 py-3 text-primary"
                >
                  <option value="base">Base</option>
                  <option value="intermedio">Intermedio</option>
                  <option value="avanzato">Avanzato</option>
                </select>
              </div>
              <Input label="N. chiuse" type="number" min={0} max={20} value={numeroChiuse} onChange={(e) => setNumeroChiuse(Number(e.target.value))} />
              <Input label="N. aperte" type="number" min={0} max={10} value={numeroAperte} onChange={(e) => setNumeroAperte(Number(e.target.value))} />
            </div>
            {aiError && <div className="text-red-300 text-sm">{aiError}</div>}
            <Button type="submit" loading={aiSending}>Genera</Button>
          </form>
        </Card>
      )}

      {loading ? (
        <div className="flex justify-center py-10"><Spinner size={28} /></div>
      ) : questions.length === 0 ? (
        <Card><p className="text-secondary">Nessuna domanda. Aggiungile a mano o generale con AI.</p></Card>
      ) : (
        <div className="space-y-3">
          {questions.map((q) => (
            <Card key={q.id}>
              <div className="flex items-start gap-3">
                <div className="flex-1 space-y-2">
                  <div className="flex items-center gap-2 flex-wrap">
                    <Badge color="#2C5F8A">{q.tipo}</Badge>
                    {q.sezione && <Badge variant="outline" color={q.coloreSezione ?? "#5B3E8A"}>{q.sezione}</Badge>}
                    {q.generataDaAi && <Badge color="#5B3E8A">AI</Badge>}
                    {q.approvata
                      ? <Badge color="#2A7A4B">Approvata</Badge>
                      : <Badge color="#E8613C">In revisione</Badge>}
                  </div>
                  <p className="text-primary font-serif">{q.testo}</p>
                  {q.opzioni && (
                    <ul className="text-sm text-secondary space-y-1">
                      {q.opzioni.map((op, i) => (
                        <li key={i} className={i === q.rispostaCorretta ? "text-green" : ""}>
                          {String.fromCharCode(65 + i)}. {op}
                          {i === q.rispostaCorretta && " ✓"}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
                <div className="flex flex-col gap-1.5">
                  <button onClick={() => openEdit(q)} className="bg-card border border-border text-secondary px-3 py-1.5 rounded-lg font-mono text-xs hover:border-accent hover:text-accent">
                    ✎ Modifica
                  </button>
                  {!q.approvata && (
                    <button onClick={() => approve(q.id)} className="bg-green text-white px-3 py-1.5 rounded-lg font-mono text-xs hover:brightness-110">
                      ✓ Approva
                    </button>
                  )}
                  {q.generataDaAi && (
                    <button onClick={() => regenerate(q.id)} className="bg-purple text-white px-3 py-1.5 rounded-lg font-mono text-xs hover:brightness-110">
                      ↻ Rigenera
                    </button>
                  )}
                  {!q.approvata
                    ? <button onClick={() => reject(q.id)} className="bg-card border border-border text-secondary px-3 py-1.5 rounded-lg font-mono text-xs hover:border-red-500 hover:text-red-300">
                        Rifiuta
                      </button>
                    : <button onClick={() => del(q.id)} className="bg-card border border-border text-secondary px-3 py-1.5 rounded-lg font-mono text-xs hover:border-red-500 hover:text-red-300">
                        Elimina
                      </button>}
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
